import React, { useContext, useEffect, useState } from "react";
import { Button, Form, Modal, Select } from "antd";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";
import {
  ChangeTransactionWaiter,
  getEmployees,
} from "../../../network/network";
import { AppContext } from "../../../context/AppContextProvider";
import { AddonName } from "../../../configs/constants";
import {
  getEntityBranchId,
  getOrderBranchId,
  isBranchScopedWaiter,
} from "../../../utils/branchScope";
import { handleApiError, showAppError } from "../../../utils/errorHandler";

const UpdateWaiter = ({ order, reloadCallback }) => {
  const { authUser } = useContext(AppContext);
  const [modalVisible, setModalVisible] = useState(false);
  const [loadingBTN, setLoadingBTN] = useState(false);
  const [employees, setEmployees] = useState([]);
  const { t } = useTranslation();
  const [UpdateWaiterForm] = Form.useForm();

  useEffect(() => {
    setLoadingBTN(true);
    getEmployees()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setEmployees(
          response.data.data.owner.concat(response.data.data.employees),
        );
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setLoadingBTN(false);
      });
  }, [t]);

  const is_waiter_management_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.WaiterManagement,
  );

  const orderBranchId = getOrderBranchId(
    order,
    getEntityBranchId(authUser),
  );

  const branchWaiters = employees.filter((employee) =>
    isBranchScopedWaiter(employee, orderBranchId),
  );

  const waiterOptions = branchWaiters.map((employee) => ({
    label: employee.name,
    value: employee.id,
  }));

  const isWaiterAllowedForOrderBranch = (waiterId) =>
    waiterOptions.some((option) => String(option.value) === String(waiterId));

  const showEditModel = () => {
    const selectedWaiterId = order.waiter?.id;

    UpdateWaiterForm.setFieldsValue({
      id: order.id,
      waiter_id:
        selectedWaiterId && isWaiterAllowedForOrderBranch(selectedWaiterId)
          ? selectedWaiterId
          : undefined,
    });
    setModalVisible(true);
  };

  const handleUpdateWaiter = () => {
    UpdateWaiterForm.validateFields()
      .then((values) => {
        if (!isWaiterAllowedForOrderBranch(values.waiter_id)) {
          UpdateWaiterForm.setFields([
            {
              name: "waiter_id",
              errors: [t("waiter_not_available_for_branch")],
            },
          ]);
          showAppError(t("waiter_not_available_for_branch"));
          return;
        }

        // Set Loading True
        setLoadingBTN(true);
        // Sending Request to API
        ChangeTransactionWaiter(values)
          .then((response) => {
            let data = response.data || response.response.data;
            if (data.success === true) {
              // Success
              NotificationWithIcon(
                "success",
                is_waiter_management_active
                  ? t("waiter_updated")
                  : t("job_person_updated"),
              );
              reloadCallback();
            } else if (data?.success === false) {
              handleApiError(response, t);
            }
            // Hide Modal
            setModalVisible(false);
          })
          .catch((error) => handleApiError(error, t))
          .finally(() => {
            // Set Loading False
            setLoadingBTN(false);
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <>
      <Button type="default" onClick={showEditModel}>
        {order.waiter
          ? is_waiter_management_active
            ? t("update_waiter")
            : t("update_job_person")
          : is_waiter_management_active
            ? t("select_waiter")
            : t("select_job_person")}
      </Button>

      <Modal
        title={
          order.waiter
            ? is_waiter_management_active
              ? t("update_waiter")
              : t("update_job_person")
            : is_waiter_management_active
              ? t("select_waiter")
              : t("select_job_person")
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
        }}
        onOk={handleUpdateWaiter}
        footer={[
          <Button
            type="default"
            key="back"
            loading={loadingBTN}
            onClick={() => setModalVisible(false)}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loadingBTN}
            onClick={handleUpdateWaiter}
          >
            {order.waiter ? t("update") : t("add")}
          </Button>,
        ]}
      >
        <Form
          name="add-waiter-form"
          layout="vertical"
          onFinish={handleUpdateWaiter}
          className="app-w-full"
          form={UpdateWaiterForm}
        >
          <Form.Item
            name="waiter_id"
            label={
              is_waiter_management_active
                ? t("select_waiter")
                : t("select_job_person")
            }
            rules={[
              {
                required: true,
                message: is_waiter_management_active
                  ? t("validate_waiter_required")
                  : t("validate_job_person_required"),
              },
            ]}
            hasFeedback
          >
            <Select
              showSearch
              allowClear
              options={waiterOptions}
              notFoundContent={
                loadingBTN ? t("loading") : t("no_waiter_found_for_branch")
              }
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="id" className="app-hidden">
            <input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UpdateWaiter;
